import React, { useEffect, useState } from 'react';
import { 
  IconPlugConnected, 
  IconPlugConnectedX, 
  IconPlugOff,
  IconAlertTriangle,
  IconRefresh
} from '@tabler/icons-react';
import socketService from '../../services/socketService';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/auth/AuthContext';

/**
 * ConnectionStatus component displays the current WebSocket connection status
 * and provides visual feedback during reconnection attempts
 */
const ConnectionStatus = () => {
  const { isAuthenticated } = useAuth();
  const [connectionState, setConnectionState] = useState('disconnected');
  const [showStatus, setShowStatus] = useState(false);
  
  useEffect(() => {
    if (!isAuthenticated) {
      setConnectionState('disconnected');
      setShowStatus(false);
      return undefined;
    }

    let hideTimer;

    // Register for connection state changes
    const unsubscribe = socketService.onConnectionStateChange((state) => {
      setConnectionState(state);
      
      // Show status when disconnected or reconnecting
      if (state === 'disconnected' || state === 'reconnecting' || state === 'failed') {
        setShowStatus(true);
      } else if (state === 'connected') {
        // Hide status after a delay when connected
        hideTimer = setTimeout(() => {
          setShowStatus(false);
        }, 3000);
      }
      
      // Show toast notifications for important state changes
      if (state === 'disconnected') {
        toast.error('Connection to server lost. Attempting to reconnect...');
      } else if (state === 'connected' && socketService.reconnectAttempts > 0) {
        toast.success('Reconnected to server');
      } else if (state === 'failed') {
        toast.error('Failed to connect to server after multiple attempts');
      }
    });
    
    // Clean up on unmount
    return () => {
      if (hideTimer) {
        clearTimeout(hideTimer);
      }
      unsubscribe();
    };
  }, [isAuthenticated]);
  
  // Define status content based on connection state
  const getStatusContent = () => {
    switch (connectionState) {
      case 'connected':
        return {
          icon: <IconPlugConnected size={18} />,
          text: 'Connected',
          color: 'bg-accent'
        };
      case 'connecting':
        return {
          icon: <IconRefresh size={18} className="animate-spin" />,
          text: 'Connecting...',
          color: 'bg-surface-3'
        };
      case 'reconnecting':
        return {
          icon: <IconRefresh size={18} className="animate-spin" />,
          text: `Reconnecting (${socketService.reconnectAttempts}/${socketService.maxReconnectAttempts})...`,
          color: 'bg-warning'
        };
      case 'disconnected':
        return {
          icon: <IconPlugConnectedX size={18} />,
          text: 'Disconnected',
          color: 'bg-error'
        };
      case 'failed':
        return {
          icon: <IconPlugOff size={18} />,
          text: 'Connection Failed',
          color: 'bg-error'
        };
      default:
        return {
          icon: <IconAlertTriangle size={18} />,
          text: 'Unknown',
          color: 'bg-surface-3'
        };
    }
  };
  
  const { icon, text, color } = getStatusContent();

  if (!isAuthenticated) {
    return null;
  }
  
  // Only render if status should be shown
  if (!showStatus && connectionState === 'connected') {
    return null;
  }
  
  return (
    <div 
      className={`fixed bottom-4 right-4 z-50 flex items-center ${color} text-white px-3 py-2`}
      onClick={() => {
        if (connectionState === 'disconnected' || connectionState === 'failed') {
          socketService.connect();
          toast.success('Attempting to reconnect...');
        }
      }}
      style={{ cursor: connectionState === 'disconnected' || connectionState === 'failed' ? 'pointer' : 'default' }}
    >
      <span className="mr-2">{icon}</span>
      <span className="text-sm font-medium">{text}</span>
    </div>
  );
};

export default ConnectionStatus;
